import type { User } from "firebase/auth";

export type AuthRuntime = Pick<
  typeof import("firebase/auth"),
  | "GoogleAuthProvider"
  | "OAuthProvider"
  | "onIdTokenChanged"
  | "signInWithEmailAndPassword"
  | "signInWithRedirect"
  | "browserPopupRedirectResolver"
  | "getRedirectResult"
  | "linkWithCredential"
  | "confirmPasswordReset"
> &
  Pick<typeof import("./firebase.auth"), "auth" | "authPersistenceReady">;

let initialization: Promise<AuthRuntime> | undefined;

/** Public content can paint before the authentication SDK is downloaded. */
export function getAuthRuntime(): Promise<AuthRuntime> {
  return (initialization ??= Promise.all([
    import("firebase/auth"),
    import("./firebase.auth"),
  ]).then(([sdk, initialized]) => ({
    GoogleAuthProvider: sdk.GoogleAuthProvider,
    OAuthProvider: sdk.OAuthProvider,
    onIdTokenChanged: sdk.onIdTokenChanged,
    signInWithEmailAndPassword: sdk.signInWithEmailAndPassword,
    signInWithRedirect: sdk.signInWithRedirect,
    browserPopupRedirectResolver: sdk.browserPopupRedirectResolver,
    getRedirectResult: sdk.getRedirectResult,
    linkWithCredential: sdk.linkWithCredential,
    confirmPasswordReset: sdk.confirmPasswordReset,
    auth: initialized.auth,
    authPersistenceReady: initialized.authPersistenceReady,
  })));
}

/** Unmounting during SDK startup must never install a late observer. */
export function observeAuthToken(
  listener: (user: User | null, runtime: AuthRuntime) => Promise<void>,
  onError: (error: unknown) => void,
): () => void {
  let cancelled = false;
  let unsubscribe: (() => void) | undefined;
  void getAuthRuntime()
    .then((runtime) => {
      if (cancelled) {
        return;
      }
      unsubscribe = runtime.onIdTokenChanged(runtime.auth, async (user) => {
        try {
          await listener(user, runtime);
        } catch (error) {
          if (!cancelled) {
            onError(error);
          }
        }
      });
    })
    .catch((error) => {
      if (!cancelled) {
        onError(error);
      }
    });
  return () => {
    cancelled = true;
    unsubscribe?.();
  };
}
